import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { CheckList } from './CheckList';
import { Volume } from '../data/volumes';
interface VolumeCardProps {
  volume: Volume;
}

/** Spine colours mirror the volumes on the product mockup. */
const spines: Record<Volume['accent'], string> = {
  gold: 'bg-gold',
  blue: 'bg-blue-600',
  green: 'bg-emerald-600',
  red: 'bg-rose-700',
  purple: 'bg-violet-600',
  slate: 'bg-navy'
};

/** Compact row: the essentials at a glance, full contents one tap away. */
export function VolumeCard({
  volume
}: VolumeCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `volume-${volume.number}-details`;
  return <li className={`rounded-2xl border bg-white shadow-sm ${volume.mostUsed ? 'border-gold' : 'border-line'}`} data-id="element-278" data-mp="1h2jabfsxdo9d">
      <div className="flex gap-4 p-4" data-id="element-279" data-mp="2dk5qlo3uplop">
        <div className="relative shrink-0" data-id="element-280" data-mp="2dl74e0c94ma4">
          <img src={volume.image} alt={volume.alt} className="h-20 w-20 rounded-lg object-cover sm:h-24 sm:w-24" width={1200} height={900} loading="lazy" decoding="async" data-id="element-281" data-mp="28snr74hh7t5u" />
          <span className={`absolute inset-y-0 left-0 w-1.5 rounded-l-lg ${spines[volume.accent]}`} aria-hidden="true" data-id="element-282" data-mp="37ed3l5lr2o5x" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-283" data-mp="2xmz776p3i4h0">
          <p className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gold-text" data-id="element-284" data-mp="2xlxku62mpvjz">
            Volume {volume.number}
            {volume.mostUsed ? <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] text-navy" data-id="element-285" data-mp="2okpymmde0l69">
                Most used
              </span> : null}
          </p>
          <h3 className="mt-1 font-display text-lg font-bold leading-snug text-navy" data-id="element-286" data-mp="2ency0q7rtcc5">
            {volume.name}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-body sm:text-sm" data-id="element-287" data-mp="2jlmt70thwcxr">
            {volume.subtitle}
          </p>
        </div>
      </div>

      <button type="button" onClick={() => setIsOpen(value => !value)} aria-expanded={isOpen} aria-controls={panelId} className="flex w-full items-center justify-between gap-2 border-t border-line px-4 py-2.5 text-sm font-semibold text-gold-text focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gold-text" data-id="element-288" data-mp="cu5jhizzeyrb">
        {isOpen ? 'Hide details' : 'See what’s inside'}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2.25} aria-hidden="true" data-id="element-289" data-mp="2eedbkgowew9" />
      </button>

      <div id={panelId} className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-290" data-mp="z6hwefp8vao3">
        <div className="overflow-hidden" data-id="element-291" data-mp="pd15pkkembon">
          <div className="space-y-4 border-t border-line px-4 py-4" data-id="element-292" data-mp="ld8suvnz9jdh">
            <p className="text-sm leading-relaxed text-body" data-id="element-293" data-mp="lawe0rdinp4n">{volume.solves}</p>
            <CheckList items={volume.covers} data-id="element-294" data-mp="1hwg6f1clrdud" />
            <p className="rounded-lg bg-canvas p-3 text-sm leading-relaxed text-body" data-id="element-295" data-mp="1d4exnpen8593">
              <span className="font-bold text-navy" data-id="element-296" data-mp="1d2c08kb5v9uc">Result: </span>
              {volume.result}
            </p>
          </div>
        </div>
      </div>
    </li>;
}